import React, { useState, useMemo } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, IconButton, Chip, Box, Typography, TablePagination,
  Skeleton, Grid, TextField, InputAdornment, Tooltip
} from '@mui/material';
import { Visibility, Edit, Delete, Search, Clear } from '@mui/icons-material';
import './DynamicTable.css';

const DynamicTable = ({
  data = [],
  columns = [],
  onEdit,
  onDelete,
  loading = false,
  emptyMessage = "No data found",
  rowsPerPageOptions = [4 , 10, 25, 50],
  defaultRowsPerPage = 4,
  disablePagination = false,
  showBorder = true,
  enableSearch = true,
  searchPlaceholder = "Search...",
  searchKeys = null,
  searchDebounceMs = 300,
  onSearchChange,
  viewMode = false,
  allowWrap = false 
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, searchDebounceMs);

    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearch]);

  // Notify parent of search change
  React.useEffect(() => {
    if (onSearchChange) {
      onSearchChange(debouncedSearch);
    }
  }, [debouncedSearch, onSearchChange]);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!debouncedSearch.trim()) return data;

    const searchLower = debouncedSearch.toLowerCase().trim();
    const keysToSearch = searchKeys || columns.map(col => col.field);

    return data.filter(row => {
      return keysToSearch.some(key => {
        const value = row[key];
        if (value === null || value === undefined) return false;
        
        const stringValue = String(value).toLowerCase();
        return stringValue.includes(searchLower);
      });
    });
  }, [data, debouncedSearch, searchKeys, columns]);

  const paginatedData = disablePagination
    ? filteredData
    : filteredData.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      );

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const colors = {
    primary: '#000000',
    success: '#005500',
    warning: '#000000'
  };

  const isSearching = debouncedSearch.trim().length > 0;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: showBorder ? `1px solid ${colors.primary}20` : 'none',
        overflow: 'hidden',
        background: '#f8f9fa'
      }}
      className="dynamic-table-container"
    >
     {/* ========== PAGINATION HEADER WITH SEARCH BAR FIXED ========== */}
      {!disablePagination && !loading && data.length > 0 && (
        <Box
          sx={{
            p: 2,
            pt: enableSearch ? 1 : 2,
            bgcolor: `${colors.primary}08`,
            borderBottom: `1px solid ${colors.primary}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          {/* LEFT SIDE: Search bar + Showing entries */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'nowrap',
              minWidth: 0,
            }}
          >
            {/* SEARCH BAR */}
            {enableSearch && (
              <Box sx={{ maxWidth: 350, width: '100%' }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ mr: 0.5 }}>
                        <Search sx={{ color: '#2e7d32', fontSize: '1.2rem' }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm ? (
                      <InputAdornment position="end">
                        <Tooltip title="Clear search">
                          <IconButton
                            size="small"
                            onClick={handleClearSearch}
                            sx={{
                              color: '#555',
                              p: 0.5,
                              transition: 'color 0.3s ease',
                              '&:hover': { color: '#2e7d32' },
                            }}
                          >
                            <Clear sx={{ fontSize: '1.2rem' }} />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ) : null,
                    sx: {
                      bgcolor: 'white',
                      borderRadius: 3,
                      fontSize: '0.9rem',
                      height: 40,
                      py: 0,
                      px: 1.5,
                      boxShadow: '0 2px 6px rgb(0 0 0 / 0.08)',
                      transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
                      '& fieldset': {
                        borderColor: '#a5d6a7',
                        borderWidth: 1.5,
                        borderRadius: 3,
                      },
                      '&:hover fieldset': {
                        borderColor: '#388e3c',
                        boxShadow: '0 0 8px #81c784',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#2e7d32',
                        borderWidth: 2,
                        boxShadow: '0 0 10px #4caf50',
                      },
                      '& input': {
                        padding: '8px 0',
                      },
                    },
                  }}
                />
              </Box>
            )}

            {/* SHOWING ENTRIES TEXT */}
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'nowrap',
                fontWeight: 600,
                color: colors.primary,
                fontSize: '0.85rem',
                minWidth: 0,
              }}
            >
              Showing {filteredData.length === 0 ? 0 : page * rowsPerPage + 1} to{' '}
              {Math.min((page + 1) * rowsPerPage, filteredData.length)} of {filteredData.length} entries
            </Typography>
          </Box>

          {/* RIGHT SIDE: Pagination Controls */}
          <TablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={Math.min(page, filteredData.length > 0 ? Math.floor((filteredData.length - 1) / rowsPerPage) : 0)}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                color: colors.primary,
                fontWeight: 500,
              },
              '& .MuiTablePagination-select, & .MuiTablePagination-input': {
                color: colors.primary,
              },
              '& .MuiTablePagination-actions button': {
                color: colors.primary,
                '&:hover': {
                  bgcolor: `${colors.primary}20`,
                },
              },
            }}
          />
        </Box>
      )}

      {/* ========== TABLE ========== */}
      <TableContainer sx={{ maxHeight: disablePagination ? 'auto' : 600 }}>
        <Table stickyHeader sx={{ tableLayout: "auto" }}>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.field}
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    ...(allowWrap && {
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      maxWidth: 200,
                      verticalAlign: "top",
                    }),
                    background: colors.success,
                    color: 'white',
                    border: 'none',
                    py: 2.5
                  }}
                >
                  {col.headerName}
                </TableCell>
              ))}
              {/* ✅ ACTIONS HEADER: Automatically hides if no onEdit/onDelete */}
              {(onEdit || onDelete) && (
                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    background: colors.success,
                    color: 'white',
                    border: 'none',
                    py: 2.5
                  }}
                >
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              Array.from({ length: Math.min(rowsPerPage, 5) }).map((_, index) => (
                <TableRow key={`skeleton-${index}`}>
                  {columns.map((col) => (
                    <TableCell key={`sk-${col.field}-${index}`}>
                      <Skeleton variant="rectangular" height={45} sx={{ borderRadius: 1, bgcolor: `${colors.primary}10` }} />
                    </TableCell>
                  ))}
                  {/* ✅ FIX: Wrapped skeleton in condition to prevent column count mismatch */}
                  {(onEdit || onDelete) && (
                    <TableCell>
                      <Skeleton variant="circular" width={36} height={36} sx={{ bgcolor: `${colors.primary}10` }} />
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : paginatedData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (onEdit || onDelete ? 1 : 0)}
                  align="center"
                  sx={{ py: 12, border: 'none' }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    {isSearching ? (
                      <>
                        <Typography variant="h4" gutterBottom>
                         
                        </Typography>
                        <Typography variant="h6" gutterBottom sx={{ color: colors.primary, fontWeight: 600 }}>
                          No results found
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                          No records match "<strong>{debouncedSearch}</strong>"
                        </Typography>
                        <Chip
                          label="Clear search"
                          onClick={handleClearSearch}
                          variant="outlined"
                          sx={{
                            borderColor: colors.success,
                            color: colors.success,
                            '&:hover': {
                              bgcolor: `${colors.success}10`,
                              borderColor: colors.success
                            }
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <Typography variant="h6" gutterBottom sx={{ color: colors.warning }}>
                          {emptyMessage}
                        </Typography>
                      </>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row, index) => (
                <TableRow
                  key={row.id || `row-${page * rowsPerPage + index}`}
                  hover
                  sx={{
                    '&:hover': { bgcolor: `${colors.primary}04` },
                    transition: 'all 0.2s ease'
                  }}
                >
                  {columns.map((col) => (
                    <TableCell
                      key={col.field}
                      sx={{
                        py: 2.5,
                        borderBottom: `1px solid ${colors.primary}10`,
                        fontSize: '0.9rem',
                        ...(allowWrap && {
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          verticalAlign: "top",
                        })
                      }}
                    >
                      {col.render ? (
                        col.render(row)
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{
                            color: row[col.field] ? 'text.primary' : `${colors.warning}70`,
                            fontWeight: row[col.field] ? 500 : 400
                          }}
                        >
                          {row[col.field] || 'N/A'}
                        </Typography>
                      )}
                    </TableCell>
                  ))}

                  {/* ✅ ACTIONS BODY: Automatically hides if no onEdit/onDelete */}
                  {(onEdit || onDelete) && (
                    <TableCell sx={{ borderBottom: `1px solid ${colors.primary}10` }}>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {onEdit && (
                          <IconButton
                            size="small"
                            sx={{
                              color: colors.success,
                              bgcolor: `${colors.success}15`,
                              '&:hover': { bgcolor: `${colors.success}25` }
                            }}
                            onClick={() => onEdit(row)}
                          >
                              {viewMode ? <Visibility fontSize="small" /> : <Edit fontSize="small" />}
                          </IconButton>
                        )}
                        {onDelete && (
                          <IconButton
                            size="small"
                            sx={{
                              color: colors.warning,
                              bgcolor: `${colors.warning}15`,
                              '&:hover': { bgcolor: `${colors.warning}25` }
                            }}
                            onClick={() => onDelete(row)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default DynamicTable;